import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MatButtonModule} from '@angular/material/button';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatInputModule} from '@angular/material/input';
import {MatIconModule} from '@angular/material/icon';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {DateTime} from 'luxon';
import {reportDay} from '../../../shared/report-day';
import {REFUND_PROCESSING_FEES_DEFINITION, REFUND_PROCESSING_FEES_LABEL, REFUND_PROCESSING_FEES_TOOLTIP} from '../../../shared/report-constants';
import {ContainerComponent} from '@coreui/angular';
import {IGameQueryResult} from '../../../interfaces/IGameQueryResult';
import {IGameInstance} from '../../../interfaces/IGameInstance';
import {GameService} from '../../../services/game.service';
import {GameInstanceService} from '../../../services/game-instance.service';
import {ActiveUserService} from '../../../services/active-user.service';
import {ReportingService} from '../../../services/reporting.service';
import {SnackbarService} from '../../../services/snackbar.service';
import {UtilService} from '../../../services/util.service';
import {UIStateEnum} from '../../../enum/UIStateEnum';
import {IDynamicReportParams, IMultiGameDynamicReportParams} from '../../../interfaces/reporting/IDynamicReportParams';
import {IDynamicReportResponse, IHostAdminFeeAllocation, IHostPayableLine} from '../../../interfaces/reporting/IDynamicReportResponse';
import {ICharityBalance} from '../../../interfaces/charity/ICharityBalance';
import {
  IMultiGameCharityBalance,
  IMultiGameCharityBreakdown,
  IMultiGameDynamicReportGame,
  IMultiGameDynamicReportResponse,
  IMultiGameDynamicReportTotals
} from '../../../interfaces/reporting/IMultiGameDynamicReportResponse';
import {PipesModule} from '../../../pipes/pipes.module';
import {FormatCurrencyPipe} from '../../../pipes/format-currency.pipe';
import * as pdfMake from 'pdfmake/build/pdfmake';
import * as pdfFonts from 'pdfmake/build/vfs_fonts';
import {Content, TDocumentDefinitions} from 'pdfmake/interfaces';
import {PermissionsService} from '../../../services/permissions.service';
import {PermissionTypes} from '../../../enum/PermissionTypes';
import {IDynamicReportPrizeWinner} from '../../../interfaces/reporting/IDynamicReportPrizeWinner';

(<any>pdfMake).vfs = (<any>pdfFonts).pdfMake.vfs;

interface IFieldToggle { key: keyof IDynamicReportParams; label: string; }
interface IFieldGroup { title: string; fields: IFieldToggle[]; }
interface IStatTile { label: string; value: number; currency: boolean; }
/**
 * The figures the tiles and the metric tables read. A single game's report and a multi-game combined total both
 * satisfy it, so one metric list drives the tiles, the per-draw table and the per-game table alike.
 */
type IMetricSource = Pick<IDynamicReportResponse,
  'GrossSales' | 'NetCharityContribution' | 'PrizeAmount' | 'PrizesAwarded' | 'CausableFees' | 'StripeFees' | 'RefundFees' |
  'AdminFees' | 'TicketsPlayed' | 'ActivePlayers' | 'TotalDraws' | 'TotalOwedToCharities' | 'PotCarriedIn' | 'PotAddedFromSales' |
  'JackpotTotal' | 'PotCarriedForward' | 'PotAdjustments' | 'OrganizationShare' | 'TotalExpenses'>;
/** A metric column: `perDraw` marks it as additive/meaningful in the per-draw breakdown table. */
// periodLabel: the name a figure takes when it covers several draws rather than one.
interface IMetricCol { label: string; periodLabel?: string; currency: boolean; perDraw: boolean; get: (r: IMetricSource) => number | undefined | null; }
type CharitySortKey = 'CharityName' | 'Game' | 'GrossContribution' | 'RefundAmount' | 'DisputeAmount' | 'NetContribution' | 'NumberOfSupporters' | 'GrossSales' | 'CausableFees';

/**
 * ReportShellComponent — the unified reporting workspace (Phase 1: custom report, date range).
 * One filter bar (scope · timeframe · fields) over one results surface (stat tiles + charity grid
 * + CSV export), driven by the dynamic-report engine. Presets, single-draw, trend line, PDF and
 * the all-games scope build on top of this shell.
 */
@Component({
  selector: 'app-report-shell',
  standalone: true,
  imports: [CommonModule, FormsModule, MatCheckboxModule, MatButtonModule, MatProgressSpinnerModule, MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule, MatDatepickerModule, ContainerComponent, PipesModule],
  templateUrl: './report-shell.component.html',
  styleUrls: ['./report-shell.component.scss', './report-shell.results.scss']
})
export class ReportShellComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private gameService = inject(GameService);
  private gameInstanceService = inject(GameInstanceService);
  private activeUserService = inject(ActiveUserService);
  private reportingService = inject(ReportingService);
  private snackbar = inject(SnackbarService);
  private utilService = inject(UtilService);
  private permissionsService = inject(PermissionsService);

  protected readonly uiStateForTemplate = UIStateEnum;
  protected readonly refundFeesLabel = REFUND_PROCESSING_FEES_LABEL;
  protected readonly refundFeesTooltip = REFUND_PROCESSING_FEES_TOOLTIP;
  protected readonly refundFeesDefinition = REFUND_PROCESSING_FEES_DEFINITION;
  protected readonly charitySalesTooltip =
    'Each purchase is split evenly across the charities it supported, when it is made. Causable fees are before tax.';
  protected uiState: UIStateEnum = UIStateEnum.ShowData;

  // Scope from route data: game scope uses the active game; global (All Games) scope lets the user pick from a dropdown
  // so reporting is reachable there too (a true cross-game aggregate is a later phase).
  protected isCrossGame = this.route.snapshot.data['isCrossGame'] === true;
  protected readonly adminGames: IGameQueryResult[] = this.gameService.getAllAvailableGames();
  protected selectedGame: IGameQueryResult | null = this.isCrossGame ? null : this.gameService.activeGame();
  protected selectedGameId = '';

  // Cross-game scope: any number of games at once (#1529). One game reports exactly as it always has; several are
  // reported one by one on the API side and combined, so the per-game rows and the total can never disagree.
  protected selectedGameIds: string[] = [];
  protected multiResult: IMultiGameDynamicReportResponse | null = null;

  // Timeframe: a date range (aggregates every draw in the window) or a single draw (game instance).
  protected timeframe: 'DateRange' | 'SingleDraw' = 'DateRange';
  protected instances: IGameInstance[] = [];
  protected instancesLoaded = false;
  protected selectedInstanceId = '';
  protected selectedInstance: IGameInstance | null = null;

  // Per-draw gross-sales series for the date-range trend line.
  protected trend: {date: string; gross: number}[] = [];
  private readonly CHART_W = 600;
  private readonly CHART_H = 110;
  /** Mirrors the endpoint's cap, so the selection is refused here with a reason instead of coming back as a 400. */
  private readonly MAXIMUM_GAMES_PER_REPORT = 100;

  ngOnInit(): void {
    // Cross-game with a single available game: auto-select it (no point showing a one-option picker).
    if (this.isCrossGame && this.adminGames.length === 1) {
      this.onGamesChosen([this.adminGames[0].Id]);
    } else if (this.selectedGame) {
      this.selectedGameId = this.selectedGame.Id;
      this.loadInstances();
    }
  }

  /**
   * The cross-game picker. One game drops straight into the single-game report; several switch the whole surface to
   * the combined one, which has no draw to pick and no payout rule to apply.
   */
  onGamesChosen(gameIdsP: string[]): void {
    this.selectedGameIds = gameIdsP;
    this.result = null;
    this.multiResult = null;

    if (gameIdsP.length === 1) {
      this.onGameIdChosen(gameIdsP[0]);
      return;
    }

    this.selectedGame = null;
    this.selectedGameId = '';
    this.selectedInstance = null;
    this.selectedInstanceId = '';
    this.instances = [];
    this.instancesLoaded = false;
    this.timeframe = 'DateRange';
    this.fields['includeHostPayoutRules'] = false;
  }

  protected get isMultiGame(): boolean {
    return this.selectedGameIds.length > 1;
  }

  protected get allGamesSelected(): boolean {
    return this.adminGames.length > 0 && this.selectedGameIds.length === this.adminGames.length;
  }

  /** Reports on everything the admin can see - the "how much did we raise altogether" question (#1529). */
  toggleAllGames(): void {
    if (!this.allGamesSelected && this.adminGames.length > this.MAXIMUM_GAMES_PER_REPORT) {
      this.snackbar.openErrorSnackBar(this.tooManyGamesMessage(this.adminGames.length));
      return;
    }

    this.onGamesChosen(this.allGamesSelected ? [] : this.adminGames.map(gameP => gameP.Id));
  }

  // Says which number is the problem and what to do, rather than leaving the reader with the generic failure.
  private tooManyGamesMessage(chosenP: number): string {
    return `A report can cover at most ${this.MAXIMUM_GAMES_PER_REPORT} games at once, and ${chosenP} are selected. Narrow the selection.`;
  }

  /** Whether there is anything to report on: one game, or several. */
  protected get hasScope(): boolean {
    return !!this.selectedGame || this.isMultiGame;
  }

  onGameIdChosen(gameIdP: string): void {
    this.selectedGameId = gameIdP;
    this.selectedGame = this.adminGames.find(g => g.Id === gameIdP) ?? null;
    this.selectedInstance = null;
    this.selectedInstanceId = '';
    this.instances = [];
    this.instancesLoaded = false;
    this.result = null;
    this.multiResult = null;
    if (this.selectedGame) this.loadInstances();
  }

  // Load the game's draws for the single-draw dropdown (most-recently-ended first) and the
  // date-range "draws in range" count.
  private loadInstances(): void {
    if (!this.selectedGame) return;
    this.instancesLoaded = false;
    this.gameInstanceService.getGameInstancesByGameId(this.selectedGame.Id, this.activeUserService.activeUser().Id).subscribe({
      next: (res) => {
        this.instances = (res ?? []).slice().sort((a, b) =>
          new Date(b.EndedOn ?? b.StartedOn).getTime() - new Date(a.EndedOn ?? a.StartedOn).getTime());
        this.instancesLoaded = true;
      },
      error: () => { this.instances = []; this.instancesLoaded = true; }
    });
  }

  // How many completed draws fall within the chosen date range (drawn/ended, in local time — matching
  // how draw dates are displayed). Drives the "N draws in this range" hint under the date pickers.
  protected get drawsInRange(): number {
    if (this.timeframe !== 'DateRange' || !this.startDate || !this.endDate) return 0;
    const start = new Date(this.startDate + 'T00:00:00').getTime();
    const end = new Date(this.endDate + 'T23:59:59.999').getTime();
    return this.instances.filter(i => {
      if (!i.EndedOn) return false;
      const drawn = new Date(i.EndedOn).getTime();
      return drawn >= start && drawn <= end;
    }).length;
  }

  onInstanceChosen(instanceIdP: string): void {
    this.selectedInstanceId = instanceIdP;
    this.selectedInstance = this.instances.find(i => i.Id === instanceIdP) ?? null;
    this.result = null;
    this.multiResult = null;
  }

  setTimeframe(timeframeP: 'DateRange' | 'SingleDraw'): void {
    this.timeframe = timeframeP;
    this.result = null;
    this.multiResult = null;
  }

  // Default to the last full month so a first run returns something useful.
  protected startDate = ReportShellComponent.isoDaysAgo(30);
  protected endDate = ReportShellComponent.isoDaysAgo(0);
  protected startDateValue: DateTime | null = DateTime.fromISO(this.startDate);
  protected endDateValue: DateTime | null = DateTime.fromISO(this.endDate);

  protected onStartDateChosen(valueP: DateTime | null): void {
    this.startDateValue = valueP;
    this.startDate = valueP?.isValid ? valueP.toISODate()! : '';
  }

  protected onEndDateChosen(valueP: DateTime | null): void {
    this.endDateValue = valueP;
    this.endDate = valueP?.isValid ? valueP.toISODate()! : '';
  }

  protected result: IDynamicReportResponse | null = null;

  // The field vocabulary (from the !1195 dynamic report), grouped for the picker.
  protected readonly fieldGroups: IFieldGroup[] = [
    {title: 'Sales & revenue', fields: [
      {key: 'includeGrossSales', label: 'Gross sales'},
      {key: 'includeNetCharityContribution', label: 'Net charity contribution'},
      {key: 'includePrizeAmount', label: 'Prizes (allocated and awarded)'},
    ]},
    {title: 'Fees & tax', fields: [
      {key: 'includeCausableFees', label: 'Causable fees (+ tax)'},
      {key: 'includeStripeFees', label: 'Stripe fees'},
      {key: 'includeDisputeFees', label: 'Dispute fees'},
      {key: 'includeRefundFees', label: REFUND_PROCESSING_FEES_LABEL},
      {key: 'includeAdminFees', label: 'Admin fees'},
    ]},
    {title: 'Counts', fields: [
      {key: 'includeTicketCounts', label: 'Ticket counts'},
      {key: 'includePlayerCounts', label: 'Player counts'},
      {key: 'includeDrawCounts', label: 'Draw counts'},
    ]},
    {title: 'Charities', fields: [
      {key: 'includeCharityBreakdown', label: 'Per-charity breakdown'},
      {key: 'includeCharityBalances', label: 'Balances (earned / paid / owed)'},
    ]},
    {title: 'Payout rules', fields: [
      {key: 'includeHostPayoutRules', label: 'Host fee split & payable'},
    ]},
    {title: 'Lottery licence', fields: [
      {key: 'includeLicenceFigures', label: 'Prize pot, organization share & expenses'},
      {key: 'includePrizeWinners', label: 'Prize winners (name, phone, prize)'},
    ]},
  ];

  private static readonly ALL_FIELD_KEYS = [
    'includeGrossSales', 'includeNetCharityContribution', 'includePrizeAmount',
    'includeCausableFees', 'includeStripeFees', 'includeDisputeFees', 'includeRefundFees', 'includeAdminFees',
    'includeTicketCounts', 'includePlayerCounts', 'includeDrawCounts', 'includeCharityBreakdown',
    'includeCharityBalances', 'includeHostPayoutRules', 'includeLicenceFigures', 'includePrizeWinners',
  ];

  private static fieldsFrom(enabledP: string[]): Record<string, boolean> {
    const out: Record<string, boolean> = {};
    for (const k of ReportShellComponent.ALL_FIELD_KEYS) out[k] = enabledP.includes(k);
    return out;
  }

  // Named reports = saved field-sets. "Custom" leaves the picker as-is. The Charley presets mirror
  // the columns of the weekly non-certified CSV pulls.
  protected readonly presets: {key: string; label: string; enabled: string[]}[] = [
    {key: 'comprehensive', label: 'Comprehensive', enabled: [
      'includeGrossSales', 'includeNetCharityContribution', 'includePrizeAmount', 'includeCausableFees',
      'includeStripeFees', 'includeDisputeFees', 'includeRefundFees', 'includeAdminFees',
      'includeTicketCounts', 'includePlayerCounts', 'includeDrawCounts', 'includeCharityBreakdown']},
    {key: 'charley-totals', label: 'Charley — All-Game Totals', enabled: [
      'includeGrossSales', 'includeCausableFees', 'includeStripeFees', 'includePrizeAmount',
      'includeNetCharityContribution', 'includeAdminFees', 'includeTicketCounts', 'includePlayerCounts',
      'includeDrawCounts']},
    {key: 'charley-charities', label: 'Charley — All-Game Charities', enabled: [
      'includeNetCharityContribution', 'includeRefundFees', 'includeCharityBreakdown']},
    {key: 'balances', label: 'Charity / Group Balances', enabled: ['includeCharityBalances']},
    {key: 'host-payout', label: 'Host Payout (fee split & payable)', enabled: [
      'includeHostPayoutRules', 'includeCharityBreakdown']},
  ];

  protected selectedPresetKey = 'comprehensive';

  // Default field selection = the Comprehensive preset.
  protected fields: Record<string, boolean> =
    ReportShellComponent.fieldsFrom(['includeGrossSales', 'includeNetCharityContribution', 'includePrizeAmount',
      'includeCausableFees', 'includeStripeFees', 'includeDisputeFees', 'includeRefundFees', 'includeAdminFees',
      'includeTicketCounts', 'includePlayerCounts', 'includeDrawCounts', 'includeCharityBreakdown']);

  applyPreset(keyP: string): void {
    this.selectedPresetKey = keyP;
    if (keyP === 'custom') return;
    const preset = this.presets.find(p => p.key === keyP);
    if (!preset) return;

    this.fields = ReportShellComponent.fieldsFrom(preset.enabled);

    // The picker hides what cannot be combined, so a preset must not be able to set it behind the picker's back.
    for (const key of Object.keys(this.fields)) {
      if (!this.fieldIsAvailable(key)) this.fields[key] = false;
    }
  }

  onFieldToggled(): void {
    // A manual change means the selection no longer matches a named preset.
    this.selectedPresetKey = 'custom';
  }

  /** Hidden rather than disabled: a field that cannot be combined is not a choice the reader has to weigh. */
  protected fieldIsAvailable(keyP: string): boolean {
    if (keyP === 'includePrizeWinners') return this.canSeePrizeWinners;
    return !(this.isMultiGame && keyP === 'includeHostPayoutRules');
  }

  /** Winners' names and phones are lottery licence report content, so they need its permission on every game reported. */
  protected get canSeePrizeWinners(): boolean {
    const game_ids = this.isMultiGame ? this.selectedGameIds : (this.selectedGameId ? [this.selectedGameId] : []);
    return game_ids.length
      ? game_ids.every(gameIdP => this.permissionsService.userHasPermissionForGame(PermissionTypes.ViewLotteryLicenseReport, gameIdP))
      : this.permissionsService.userHasCrossGamePermission(PermissionTypes.ViewLotteryLicenseReport);
  }

  protected groupIsAvailable(groupP: IFieldGroup): boolean {
    return groupP.fields.some(fieldP => this.fieldIsAvailable(fieldP.key));
  }

  // The admin's own calendar day: toISOString() is UTC's, which is already tomorrow on a PEI evening.
  private static isoDaysAgo(daysP: number): string {
    return DateTime.now().minus({days: daysP}).toISODate()!;
  }

  protected get anyFieldSelected(): boolean {
    return Object.values(this.fields).some(v => v);
  }

  /**
   * The selected fields, as the API's flags. Shared by both reports so a field cannot end up on one and not the other.
   */
  private selectedFieldFlags() {
    return {
      includeGrossSales: !!this.fields['includeGrossSales'],
      includeCausableFees: !!this.fields['includeCausableFees'],
      includeStripeFees: !!this.fields['includeStripeFees'],
      includeDisputeFees: !!this.fields['includeDisputeFees'],
      includePrizeAmount: !!this.fields['includePrizeAmount'],
      includeNetCharityContribution: !!this.fields['includeNetCharityContribution'],
      includeAdminFees: !!this.fields['includeAdminFees'],
      includeRefundFees: !!this.fields['includeRefundFees'],
      includeDrawCounts: !!this.fields['includeDrawCounts'],
      includeTicketCounts: !!this.fields['includeTicketCounts'],
      includePlayerCounts: !!this.fields['includePlayerCounts'],
      includeCharityBreakdown: !!this.fields['includeCharityBreakdown'],
      includeCharityBalances: !!this.fields['includeCharityBalances'],
      includeLicenceFigures: !!this.fields['includeLicenceFigures'],
      // Re-checked here: the field stays ticked when the game changes to one where it is hidden.
      includePrizeWinners: !!this.fields['includePrizeWinners'] && this.canSeePrizeWinners,
    };
  }

  /** The date-range checks both reports share. */
  private dateRangeIsValid(): boolean {
    if (!this.startDate || !this.endDate) {
      this.snackbar.openErrorSnackBar('Choose a start and end date');
      return false;
    }
    if (this.startDate > this.endDate) {
      this.snackbar.openErrorSnackBar('The start date must be on or before the end date');
      return false;
    }
    return true;
  }

  private runMultiGameReport(): void {
    if (!this.dateRangeIsValid()) {
      return;
    }
    if (this.selectedGameIds.length > this.MAXIMUM_GAMES_PER_REPORT) {
      this.snackbar.openErrorSnackBar(this.tooManyGamesMessage(this.selectedGameIds.length));
      return;
    }
    if (!this.anyFieldSelected) {
      this.snackbar.openErrorSnackBar('Select at least one field to include');
      return;
    }

    const params: IMultiGameDynamicReportParams = {
      gameIds: this.selectedGameIds,
      dateSelectionType: 'DateRange',
      startDate: this.startDate,
      endDate: this.endDate,
      ...this.selectedFieldFlags(),
    };

    this.uiState = UIStateEnum.ShowLoading;
    this.result = null;
    this.multiResult = null;
    // A gross-sales-per-draw line is one game's shape; several games' draws on one axis would be a different chart.
    this.trend = [];
    this.viewMode = 'totals';

    this.reportingService.getMultiGameDynamicReport(params).subscribe({
      next: (res) => {
        if (!res) {
          this.snackbar.openErrorSnackBar('The report returned no data. Refresh the page and try again.');
          this.uiState = UIStateEnum.ShowData;
          return;
        }
        this.multiResult = res;
        this.charityGameFilter = '';
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.snackbar.openErrorSnackBar(
          err?.status === 404
            ? 'One of the selected games could not be found. Refresh the page and try again.'
            : err?.status === 403
              ? 'You do not have permission to report on all of the selected games.'
              : 'Failed to generate the report. Please adjust the filters and try again.');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  protected runReport(): void {
    if (this.isMultiGame) {
      this.runMultiGameReport();
      return;
    }

    if (!this.selectedGame?.Id) {
      this.snackbar.openErrorSnackBar('Choose a game to report on');
      return;
    }
    if (this.timeframe === 'DateRange') {
      if (!this.dateRangeIsValid()) {
        return;
      }
    } else if (!this.selectedInstance?.Id) {
      this.snackbar.openErrorSnackBar('Choose a draw');
      return;
    }
    if (!this.anyFieldSelected) {
      this.snackbar.openErrorSnackBar('Select at least one field to include');
      return;
    }

    const params: IDynamicReportParams = {
      gameId: this.selectedGame.Id,
      dateSelectionType: this.timeframe,
      startDate: this.timeframe === 'DateRange' ? this.startDate : undefined,
      endDate: this.timeframe === 'DateRange' ? this.endDate : undefined,
      gameInstanceId: this.timeframe === 'SingleDraw' ? this.selectedInstance!.Id : undefined,
      ...this.selectedFieldFlags(),
      includeHostPayoutRules: !!this.fields['includeHostPayoutRules'],
      // Date-range: also pull the per-draw breakdown so the "By draw" view is instant to toggle.
      includeInstanceBreakdown: this.timeframe === 'DateRange',
    };

    this.uiState = UIStateEnum.ShowLoading;
    this.result = null;
    this.multiResult = null;
    this.viewMode = 'totals';
    this.loadTrend();
    this.reportingService.getDynamicReport(params).subscribe({
      next: (res) => {
        // Defensive: a success response with no body would otherwise drop the pane back to the
        // "choose your fields" placeholder, which reads as though the run never happened.
        if (!res) {
          this.snackbar.openErrorSnackBar('The report returned no data. Refresh the page and try again.');
          this.uiState = UIStateEnum.ShowData;
          return;
        }
        this.result = res;
        this.uiState = UIStateEnum.ShowData;
      },
      error: (err) => {
        this.snackbar.openErrorSnackBar(
          err?.status === 404
            ? 'The selected game or draw could not be found. Refresh the page and try again.'
            : 'Failed to generate the report. Please adjust the filters and try again.');
        this.uiState = UIStateEnum.ShowData;
      }
    });
  }

  // Totals vs per-draw breakdown of the results surface.
  protected viewMode: 'totals' | 'byDraw' = 'totals';

  setViewMode(modeP: 'totals' | 'byDraw'): void {
    this.viewMode = modeP;
  }

  // Single source of truth for the metric set, driving both the KPI tiles and the breakdown columns. perDraw =
  // meaningful per draw; distinct counts and draw counts are aggregate-only.
  private readonly jackpotCol: IMetricCol =
    {label: 'Jackpot', periodLabel: 'Prize pot available', currency: true, perDraw: true, get: r => r.JackpotTotal};

  private readonly metricCols: IMetricCol[] = [
    {label: 'Gross sales', currency: true, perDraw: true, get: r => r.GrossSales},
    {label: 'Net charity', currency: true, perDraw: true, get: r => r.NetCharityContribution},
    {label: 'Allocated to prizes', currency: true, perDraw: true, get: r => r.PrizeAmount},
    {label: 'Prizes awarded', currency: true, perDraw: true, get: r => r.PrizesAwarded},
    {label: 'Causable fees', currency: true, perDraw: true, get: r => r.CausableFees},
    {label: 'Stripe fees', currency: true, perDraw: true, get: r => r.StripeFees},
    {label: REFUND_PROCESSING_FEES_LABEL, currency: true, perDraw: true, get: r => r.RefundFees},
    {label: 'Admin fees', currency: true, perDraw: true, get: r => r.AdminFees},
    {label: 'Pot carried in', currency: true, perDraw: true, get: r => r.PotCarriedIn},
    {label: 'Added to pot from sales', currency: true, perDraw: true, get: r => r.PotAddedFromSales},
    this.jackpotCol,
    {label: 'Pot carried forward', currency: true, perDraw: true, get: r => r.PotCarriedForward},
    {label: 'Pot adjustments', currency: true, perDraw: true, get: r => r.PotAdjustments},
    {label: 'Organization share', currency: true, perDraw: true, get: r => r.OrganizationShare},
    {label: 'Total expenses', currency: true, perDraw: true, get: r => r.TotalExpenses},
    {label: 'Tickets played', currency: false, perDraw: true, get: r => r.TicketsPlayed},
    {label: 'Active players', currency: false, perDraw: false, get: r => r.ActivePlayers},
    {label: 'Total draws', currency: false, perDraw: false, get: r => r.TotalDraws},
    // A running ledger balance that ignores the chosen dates.
    {label: 'Total owed to charities (all time)', currency: true, perDraw: false, get: r => r.TotalOwedToCharities},
  ];

  private labelFor(colP: IMetricCol, isPeriodP: boolean): string {
    return isPeriodP ? colP.periodLabel ?? colP.label : colP.label;
  }

  /** Only the requested-and-present metrics become tiles. */
  private tilesFor(sourceP: IMetricSource | null | undefined, isPeriodP: boolean): IStatTile[] {
    if (!sourceP) return [];
    return this.metricCols
      .filter(c => c.get(sourceP) !== undefined && c.get(sourceP) !== null)
      .map(c => ({label: this.labelFor(c, isPeriodP), value: c.get(sourceP)!, currency: c.currency}));
  }

  private get isPeriodReport(): boolean {
    return this.result?.DateSelectionType !== 'SingleDraw';
  }

  protected get statTiles(): IStatTile[] {
    return this.tilesFor(this.result, this.isPeriodReport);
  }

  // The licence notes name the jackpot figure the way its tile does.
  protected get singleJackpotLabel(): string {
    return this.labelFor(this.jackpotCol, this.isPeriodReport);
  }

  protected get combinedJackpotLabel(): string {
    return this.labelFor(this.jackpotCol, true);
  }

  // ---- Multi-game surface (#1529) ----

  protected get combined(): IMultiGameDynamicReportTotals | null {
    return this.multiResult?.Combined ?? null;
  }

  protected get combinedTiles(): IStatTile[] {
    return this.tilesFor(this.combined, true);
  }

  protected get perGameRows(): IMultiGameDynamicReportGame[] {
    return this.multiResult?.Games ?? [];
  }

  /** The per-game table shows the same metrics as the combined tiles, so a reader can add the column up themselves. */
  protected get perGameColumns(): IMetricCol[] {
    const c = this.combined;
    if (!c) return [];
    return this.metricCols
      .filter(colP => colP.get(c) !== undefined && colP.get(c) !== null)
      .map(colP => ({...colP, label: this.labelFor(colP, true)}));
  }

  protected get combinedCharityRows(): IMultiGameCharityBreakdown[] {
    return this.combined?.CharityBreakdown ?? [];
  }

  /** '' shows every selected game merged; a game id shows that game's own figures, not its share of a merged row. */
  protected charityGameFilter = '';
  protected charitySort: { key: CharitySortKey; dir: 'asc' | 'desc' } | null = null;

  protected get visibleCharityRows(): IMultiGameCharityBreakdown[] {
    const game = this.charityFilterGame;
    const rows: IMultiGameCharityBreakdown[] = game
      ? (game.Report.CharityBreakdown ?? []).map(cP => ({...cP, GrossSales: cP.GrossSales ?? 0, CausableFees: cP.CausableFees ?? 0, GameCount: 1, GameNames: [game.GameName]}))
      : this.combinedCharityRows;

    const sort = this.charitySort;
    if (!sort) return rows;
    const value = (rP: IMultiGameCharityBreakdown): string | number =>
      sort.key === 'CharityName' ? rP.CharityName.toLowerCase()
        : sort.key === 'Game' ? (rP.GameNames ?? []).join(', ').toLowerCase()
        : (rP[sort.key] ?? -1);
    const direction = sort.dir === 'asc' ? 1 : -1;
    return rows.slice().sort((aP, bP) => {
      const a = value(aP), b = value(bP);
      return (a < b ? -1 : a > b ? 1 : 0) * direction;
    });
  }

  protected get charityFilterGame(): IMultiGameDynamicReportGame | null {
    return this.charityGameFilter ? this.perGameRows.find(gP => gP.GameId === this.charityGameFilter) ?? null : null;
  }

  protected get visibleCharityNet(): number {
    return this.visibleCharityRows.reduce((sumP, rP) => sumP + rP.NetContribution, 0);
  }

  /** Names sort A-Z first, amounts largest first; a second click reverses. */
  protected sortCharitiesBy(keyP: CharitySortKey): void {
    if (this.charitySort?.key === keyP) {
      this.charitySort = {key: keyP, dir: this.charitySort.dir === 'asc' ? 'desc' : 'asc'};
      return;
    }
    this.charitySort = {key: keyP, dir: keyP === 'CharityName' || keyP === 'Game' ? 'asc' : 'desc'};
  }

  protected charitySortIndicator(keyP: CharitySortKey): string {
    if (this.charitySort?.key !== keyP) return '';
    return this.charitySort.dir === 'asc' ? ' ▲' : ' ▼';
  }

  protected get combinedBalanceRows(): IMultiGameCharityBalance[] {
    return this.combined?.CharityBalances ?? [];
  }

  /** True once a merged charity is remitted through different payees in different games - what blanks the column. */
  protected get anyBalancePayeeVaries(): boolean {
    return this.combinedBalanceRows.some(bP => bP.PaidThroughVaries);
  }

  /**
   * True once any merged charity spans more than one game, which is when the supporters column starts showing blanks -
   * the note under the table is what explains them.
   */
  protected get anyCharitySpansGames(): boolean {
    return this.combinedCharityRows.some(cP => cP.GameCount > 1);
  }

  protected get multiGameNames(): string {
    return this.perGameRows.map(gP => gP.GameName).join(', ');
  }

  protected get prizeWinners(): IDynamicReportPrizeWinner[] {
    return (this.multiResult ? this.combined?.PrizeWinners : this.result?.PrizeWinners) ?? [];
  }

  /** True when the licence figures were run: they cover finished draws only, which the note under the tiles says. */
  protected get hasLicenceFigures(): boolean {
    const source = this.multiResult ? this.combined : this.result;
    return source?.TotalExpenses !== undefined && source?.TotalExpenses !== null;
  }

  protected winnerName(winnerP: IDynamicReportPrizeWinner): string {
    return `${winnerP.FirstName ?? ''} ${winnerP.LastName ?? ''}`.trim();
  }

  private winnersCsv(linesP: string[], withGameP: boolean): void {
    if (!this.prizeWinners.length) return;
    linesP.push('');
    linesP.push(this.csvRow([...(withGameP ? ['Game'] : []), 'Draw date', 'Winner', 'Phone', 'Prize']));
    for (const w of this.prizeWinners) {
      linesP.push(this.csvRow([...(withGameP ? [w.GameName ?? ''] : []), w.DrawDate?.slice(0, 10), this.winnerName(w), w.Phone ?? '', w.PrizeAmount]));
    }
  }

  private winnersPdf(contentP: Content[], withGameP: boolean): void {
    if (!this.prizeWinners.length) return;
    contentP.push({text: 'Prize winners', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
    contentP.push({
      table: {
        headerRows: 1, widths: [...(withGameP ? ['auto'] : []), 'auto', '*', 'auto', 'auto'],
        body: [
          [...(withGameP ? [{text: 'Game', bold: true}] : []), {text: 'Draw date', bold: true}, {text: 'Winner', bold: true},
           {text: 'Phone', bold: true}, {text: 'Prize', bold: true}],
          ...this.prizeWinners.map(w => [
            ...(withGameP ? [w.GameName ?? ''] : []),
            w.DrawDate?.slice(0, 10) ?? '',
            this.winnerName(w),
            w.Phone ?? '',
            {text: this.money(w.PrizeAmount), alignment: 'right'},
          ]),
        ],
      },
      layout: 'lightHorizontalLines', fontSize: 9,
    });
  }

  protected get hasBreakdown(): boolean {
    return (this.result?.InstanceBreakdown?.length ?? 0) > 0;
  }

  /** Columns for the per-draw table: the additive/present metrics (money + tickets). */
  protected get breakdownColumns(): IMetricCol[] {
    const r = this.result;
    if (!r) return [];
    return this.metricCols.filter(c => c.perDraw && c.get(r) !== undefined && c.get(r) !== null);
  }

  protected get breakdownRows(): IDynamicReportResponse[] {
    return this.result?.InstanceBreakdown ?? [];
  }

  // Charity/group balances ledger (earned / paid / owed) — the sheet's "Balances" tab.
  protected get hasBalances(): boolean {
    return (this.result?.CharityBalances?.length ?? 0) > 0;
  }

  protected get balanceRows(): ICharityBalance[] {
    return this.result?.CharityBalances ?? [];
  }

  /**
   * Whether this game shows a host admin fee. A game with no rule reports no fee at all rather than a
   * zero, so absence of the figure — not a zero value — is what hides the section.
   */
  protected get hasHostAdminFee(): boolean {
    return this.result?.HostAdminFeeTotal !== undefined && this.result?.HostAdminFeeTotal !== null;
  }

  /**
   * The rule's effective date, formatted without a timezone conversion.
   *
   * Never through DatePipe: effective_from is a calendar date the API serializes as UTC midnight, so converting it
   * renders the previous day for a reader west of UTC. On a line whose whole purpose is to say from when these
   * figures were split, a date that is off by one is worse than no date — the same rule the payout ledger's
   * payment dates and the rules screen's own history already follow.
   */
  protected formatRuleEffectiveFrom(valueP: string | null | undefined): string {
    if (!valueP) {
      return '—';
    }

    const [year, month, day] = valueP.split('T')[0].split('-');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    return `${months[Number(month) - 1]} ${Number(day)}, ${year}`;
  }

  protected get hostFeeRows(): IHostAdminFeeAllocation[] {
    return this.result?.HostAdminFeeAllocations ?? [];
  }

  protected get hostPayableLines(): IHostPayableLine[] {
    return this.result?.HostPayableLines ?? [];
  }

  protected get hasHostPayable(): boolean {
    return this.result?.HostPayableAmount !== undefined && this.result?.HostPayableAmount !== null;
  }

  /** Formats a metric cell (currency vs count) for the breakdown and per-game tables. */
  // A period figure such as the prize pot available is not the sum of the per-draw cells under its column, so the
  // by-draw total row leaves it blank rather than contradict the tile.
  protected totalCell(colP: IMetricCol, rowP: IMetricSource): string {
    return colP.periodLabel ? '' : this.cell(colP, rowP);
  }

  protected cell(colP: IMetricCol, rowP: IMetricSource): string {
    const v = colP.get(rowP);
    if (v === undefined || v === null) return '—';
    return colP.currency ? this.money(v) : v.toLocaleString();
  }

  /** The combined report as CSV: the totals, then the games they are made of, then the merged charity tables. */
  private downloadMultiGameCsv(): void {
    const c = this.combined;
    if (!c) return;

    const lines: string[] = [];
    lines.push(this.csvRow(['Report', `${this.perGameRows.length} games`, this.multiGameNames]));
    lines.push(this.csvRow(['Period', this.startDate, this.endDate]));
    lines.push('');
    lines.push('Metric,Value');
    for (const t of this.combinedTiles) {
      lines.push(this.csvRow([t.label, t.value]));
    }

    const cols = this.perGameColumns;
    if (cols.length) {
      lines.push('');
      lines.push(this.csvRow(['Game', ...cols.map(colP => colP.label)]));
      for (const row of this.perGameRows) {
        lines.push(this.csvRow([row.GameName, ...cols.map(colP => colP.get(row.Report) ?? '')]));
      }
      // The combined row under the games it is made of, so the file carries the arithmetic, not just its result.
      lines.push(this.csvRow([`Total (${this.perGameRows.length} games)`, ...cols.map(colP => colP.get(c) ?? '')]));
    }

    if (this.combinedCharityRows.length) {
      lines.push('');
      lines.push(this.csvRow(['Charity', 'Game', 'Gross contribution', REFUND_PROCESSING_FEES_LABEL, 'Disputes', 'Net contribution', 'Supporters',
        'Gross sales', 'Causable fees']));
      for (const charity of this.combinedCharityRows) {
        // Blank, not zero, where the charity spans games: supporters are distinct players per game and cannot be added.
        lines.push(this.csvRow([charity.CharityName, (charity.GameNames ?? []).join(', '), charity.GrossContribution, charity.RefundAmount,
          charity.DisputeAmount, charity.NetContribution, charity.NumberOfSupporters ?? '', charity.GrossSales, charity.CausableFees]));
      }
    }

    if (this.combinedBalanceRows.length) {
      lines.push('');
      lines.push(this.csvRow(['Charity/Group', 'Game', 'Paid through', 'Total earned', 'Total paid out', 'Adjustments', 'Amount owed']));
      for (const b of this.combinedBalanceRows) {
        // Blank, not a guess, where the games remit through different payees: neither one is true of the merged row.
        lines.push(this.csvRow([b.CharityName, (b.GameNames ?? []).join(', '), b.PaidThroughVaries ? '' : (b.PaidThroughPayeeName || 'Paid directly'),
          b.TotalContributions, b.TotalPayouts, b.TotalAdjustments, b.AmountOwed]));
      }
    }

    this.winnersCsv(lines, true);

    const blob = new Blob([lines.join('\n')], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${this.perGameRows.length}_games_${this.startDate}_to_${this.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  protected downloadCsv(): void {
    if (this.multiResult) {
      this.downloadMultiGameCsv();
      return;
    }

    const r = this.result;
    if (!r || !this.selectedGame) return;

    const lines: string[] = [];
    lines.push(this.csvRow(['Report', this.selectedGame.Name]));
    lines.push(this.csvRow(['Period', this.startDate, this.endDate]));
    lines.push('');
    lines.push('Metric,Value');
    for (const t of this.statTiles) {
      // t.value is already formatted for display, so money above $999.99 carries a thousands
      // separator. Unquoted, that comma splits the value across two columns.
      lines.push(this.csvRow([t.label, t.value]));
    }
    if (this.hasBreakdown) {
      const cols = this.breakdownColumns;
      lines.push('');
      lines.push(this.csvRow(['Draw', ...cols.map(c => c.label)]));
      for (const row of this.breakdownRows) {
        const drawDate = row.DrawDate ? reportDay(new Date(row.DrawDate)) : '';
        lines.push(this.csvRow([drawDate, ...cols.map(c => c.get(row) ?? '')]));
      }
      // Totals row (the aggregate) — additive columns reconcile to the per-draw rows above.
      lines.push(this.csvRow([`Total (${this.breakdownRows.length} draws)`, ...cols.map(c => c.periodLabel ? '' : c.get(r) ?? '')]));
    }

    if (r.CharityBreakdown?.length) {
      lines.push('');
      lines.push(this.csvRow(['Charity', 'Gross contribution', REFUND_PROCESSING_FEES_LABEL, 'Disputes', 'Net contribution', 'Supporters',
        'Gross sales', 'Causable fees']));
      for (const c of r.CharityBreakdown) {
        lines.push(this.csvRow([c.CharityName, c.GrossContribution, c.RefundAmount, c.DisputeAmount, c.NetContribution, c.NumberOfSupporters,
          c.GrossSales, c.CausableFees]));
      }
    }

    if (r.CharityBalances?.length) {
      lines.push('');
      lines.push(this.csvRow(['Charity/Group', 'Paid through', 'Total earned', 'Total paid out', 'Adjustments', 'Amount owed']));
      for (const b of r.CharityBalances) {
        // Adjustments included so the exported row reconciles the same way the on-screen one does: owed = earned +
        // adjustments - paid out.
        lines.push(this.csvRow([b.CharityName, b.PaidThroughPayeeName || 'Paid directly', b.TotalContributions, b.TotalPayouts, b.TotalAdjustments, b.AmountOwed]));
      }
    }

    if (r.HostAdminFeeAllocations?.length) {
      lines.push('');
      lines.push('Host admin fee recipient,Share %,Amount');
      for (const a of r.HostAdminFeeAllocations) {
        lines.push(this.csvRow([a.RecipientName, a.SharePercent, a.Amount]));
      }
      // The split's own total, so a reader can check the parts against the whole in the file itself.
      lines.push(this.csvRow(['Total host admin fee', '', r.HostAdminFeeTotal ?? 0]));
    }

    if (r.HostPayableAmount !== undefined && r.HostPayableAmount !== null) {
      lines.push('');
      lines.push('Payable to host organization,Amount');
      // The lines first, then the total: the file carries the arithmetic, not just its result.
      for (const line of r.HostPayableLines ?? []) {
        lines.push(this.csvRow([`${line.IsAddition ? '+' : '-'} ${line.Label}`, line.SignedAmount]));
      }
      lines.push(this.csvRow([r.HostPayableRecipientName ?? 'Total payable', r.HostPayableAmount]));
    }

    this.winnersCsv(lines, false);

    const blob = new Blob([lines.join('\n')], {type: 'text/csv'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `report_${this.selectedGame.Subdomain ?? 'game'}_${this.startDate}_to_${this.endDate}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  // Date-range only: fetch per-draw summaries for the trend line (a separate, existing endpoint).
  private loadTrend(): void {
    this.trend = [];
    if (this.timeframe !== 'DateRange' || !this.selectedGame || !this.startDate || !this.endDate) return;
    this.reportingService.getMultipleStandardGameInstanceReports({
      GameId: this.selectedGame.Id,
      StartDate: this.startDate,
      EndDate: this.endDate,
    }).subscribe({
      next: (rows) => {
        this.trend = (rows ?? [])
          .map(r => ({date: (r.EndDate ?? r.DrawTime) as unknown as string, gross: r.GrossSales ?? 0}))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      },
      error: () => { this.trend = []; }
    });
  }

  /** Builds the SVG line + area for the gross-sales trend, or null when there's too little data. */
  protected get chart(): {line: string; area: string; lastX: number; lastY: number} | null {
    if (this.trend.length < 2) return null;
    const n = this.trend.length;
    const max = Math.max(...this.trend.map(t => t.gross), 1);
    const x = (i: number) => (i / (n - 1)) * this.CHART_W;
    const y = (v: number) => this.CHART_H - (v / max) * (this.CHART_H - 12) - 6;
    const pts = this.trend.map((t, i) => `${x(i).toFixed(1)},${y(t.gross).toFixed(1)}`);
    return {
      line: 'M' + pts.join(' L'),
      area: `M0,${this.CHART_H} L` + pts.join(' L') + ` L${this.CHART_W},${this.CHART_H} Z`,
      lastX: x(n - 1),
      lastY: y(this.trend[n - 1].gross),
    };
  }

  // Amounts are stored in cents. Delegate to the same pipe the tiles/charity grid use so the by-draw
  // table, PDF and on-screen values all format cents -> dollars identically (was showing raw cents).
  private readonly currencyPipe = new FormatCurrencyPipe();

  private money(nP: number | undefined): string {
    return this.currencyPipe.transform(nP ?? 0);
  }

  private downloadMultiGamePdf(): void {
    const c = this.combined;
    if (!c) return;

    const cols = this.perGameColumns;
    const content: Content[] = [
      {text: `${this.perGameRows.length} games`, fontSize: 16, bold: true},
      {text: this.multiGameNames, fontSize: 9, color: '#666', margin: [0, 2, 0, 0]},
      {text: `${this.startDate} \u2013 ${this.endDate}`, fontSize: 10, color: '#666', margin: [0, 2, 0, 12]},
      {
        table: {
          headerRows: 1, widths: ['*', 'auto'],
          body: [
            [{text: 'Metric', bold: true}, {text: 'Value', bold: true}],
            ...this.combinedTiles.map(t => [t.label, {text: t.currency ? this.money(t.value) : t.value.toLocaleString(), alignment: 'right'}]),
          ],
        },
        layout: 'lightHorizontalLines',
      },
    ];

    if (cols.length) {
      content.push({text: 'By game', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', ...cols.map(() => 'auto')],
          body: [
            [{text: 'Game', bold: true}, ...cols.map(colP => ({text: colP.label, bold: true}))],
            ...this.perGameRows.map(row => [row.GameName, ...cols.map(colP => ({text: this.cell(colP, row.Report), alignment: 'right'}))]),
            [{text: 'Total', bold: true}, ...cols.map(colP => ({text: this.cell(colP, c), alignment: 'right', bold: true}))],
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    if (this.combinedCharityRows.length) {
      content.push({text: 'Charity breakdown', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [{text: 'Charity', bold: true}, {text: 'Game', bold: true}, {text: 'Gross', bold: true}, {text: REFUND_PROCESSING_FEES_LABEL, bold: true},
             {text: 'Disputes', bold: true}, {text: 'Net', bold: true}, {text: 'Supporters', bold: true}, {text: 'Gross sales', bold: true},
             {text: 'Causable fees', bold: true}],
            ...this.combinedCharityRows.map(charity => [
              charity.CharityName,
              (charity.GameNames ?? []).join(', '),
              {text: this.money(charity.GrossContribution), alignment: 'right'},
              {text: this.money(charity.RefundAmount), alignment: 'right'},
              {text: this.money(charity.DisputeAmount), alignment: 'right'},
              {text: this.money(charity.NetContribution), alignment: 'right'},
              {text: charity.NumberOfSupporters?.toLocaleString() ?? '\u2014', alignment: 'right'},
              {text: this.money(charity.GrossSales), alignment: 'right'},
              {text: this.money(charity.CausableFees), alignment: 'right'},
            ]),
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    if (this.combinedBalanceRows.length) {
      content.push({text: 'Charity / group balances', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [{text: 'Charity / group', bold: true}, {text: 'Game', bold: true}, {text: 'Paid through', bold: true},
             {text: 'Earned', bold: true}, {text: 'Paid out', bold: true}, {text: 'Owed', bold: true}],
            ...this.combinedBalanceRows.map(b => [
              b.CharityName,
              (b.GameNames ?? []).join(', '),
              b.PaidThroughVaries ? '—' : (b.PaidThroughPayeeName || 'Paid directly'),
              {text: this.money(b.TotalContributions), alignment: 'right'},
              {text: this.money(b.TotalPayouts), alignment: 'right'},
              {text: this.money(b.AmountOwed), alignment: 'right'},
            ]),
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    this.winnersPdf(content, true);

    const docDef: TDocumentDefinitions = {content, defaultStyle: {fontSize: 10}, pageMargins: [30, 30, 30, 30]};
    pdfMake.createPdf(docDef).open();
  }

  downloadPdf(): void {
    if (this.multiResult) {
      this.downloadMultiGamePdf();
      return;
    }

    const r = this.result;
    if (!r || !this.selectedGame) return;

    const content: Content[] = [
      {text: this.selectedGame.Name, fontSize: 16, bold: true},
      {text: `${r.StartDate?.slice(0, 10)} – ${r.EndDate?.slice(0, 10)}`, fontSize: 10, color: '#666', margin: [0, 2, 0, 12]},
      {
        table: {
          headerRows: 1, widths: ['*', 'auto'],
          body: [
            [{text: 'Metric', bold: true}, {text: 'Value', bold: true}],
            ...this.statTiles.map(t => [t.label, {text: t.currency ? this.money(t.value) : t.value.toLocaleString(), alignment: 'right'}]),
          ],
        },
        layout: 'lightHorizontalLines',
      },
    ];

    if (r.CharityBreakdown?.length) {
      content.push({text: 'Charity breakdown', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [{text: 'Charity', bold: true}, {text: 'Gross', bold: true}, {text: REFUND_PROCESSING_FEES_LABEL, bold: true}, {text: 'Disputes', bold: true}, {text: 'Net', bold: true}, {text: 'Supporters', bold: true},
             {text: 'Gross sales', bold: true}, {text: 'Causable fees', bold: true}],
            ...r.CharityBreakdown.map(c => [
              c.CharityName,
              {text: this.money(c.GrossContribution), alignment: 'right'},
              {text: this.money(c.RefundAmount), alignment: 'right'},
              {text: this.money(c.DisputeAmount), alignment: 'right'},
              {text: this.money(c.NetContribution), alignment: 'right'},
              {text: c.NumberOfSupporters.toLocaleString(), alignment: 'right'},
              {text: this.money(c.GrossSales), alignment: 'right'},
              {text: this.money(c.CausableFees), alignment: 'right'},
            ]),
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    if (r.CharityBalances?.length) {
      content.push({text: 'Charity / group balances', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', 'auto', 'auto', 'auto', 'auto'],
          body: [
            [{text: 'Charity / group', bold: true}, {text: 'Paid through', bold: true}, {text: 'Earned', bold: true}, {text: 'Paid out', bold: true}, {text: 'Owed', bold: true}],
            ...r.CharityBalances.map(b => [
              b.CharityName,
              // Who the cheque is made out to (#1411) — several charities owed money may be settled by one payment.
              b.PaidThroughPayeeName || 'Paid directly',
              {text: this.money(b.TotalContributions), alignment: 'right'},
              {text: this.money(b.TotalPayouts), alignment: 'right'},
              {text: this.money(b.AmountOwed), alignment: 'right'},
            ]),
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    if (r.HostAdminFeeAllocations?.length) {
      content.push({text: 'Host admin fee', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 1, widths: ['*', 'auto', 'auto'],
          body: [
            [{text: 'Recipient', bold: true}, {text: 'Share', bold: true}, {text: 'Amount', bold: true}],
            ...r.HostAdminFeeAllocations.map(a => [
              a.RecipientName,
              {text: `${a.SharePercent}%`, alignment: 'right'},
              {text: this.money(a.Amount), alignment: 'right'},
            ]),
            // The whole, under the parts — the reader can check one against the other on the page.
            [{text: 'Total', bold: true}, '', {text: this.money(r.HostAdminFeeTotal ?? 0), alignment: 'right', bold: true}],
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    if (r.HostPayableAmount !== undefined && r.HostPayableAmount !== null) {
      content.push({text: 'Payable to host organization', fontSize: 12, bold: true, margin: [0, 16, 0, 6]});
      content.push({
        table: {
          headerRows: 0, widths: ['*', 'auto'],
          body: [
            ...(r.HostPayableLines ?? []).map(l => [
              `${l.IsAddition ? '+' : '−'} ${l.Label}`,
              {text: this.money(l.SignedAmount), alignment: 'right'},
            ]),
            [{text: r.HostPayableRecipientName ?? 'Total payable', bold: true},
             {text: this.money(r.HostPayableAmount), alignment: 'right', bold: true}],
          ],
        },
        layout: 'lightHorizontalLines', fontSize: 9,
      });
    }

    this.winnersPdf(content, false);

    const docDef: TDocumentDefinitions = {content, defaultStyle: {fontSize: 10}, pageMargins: [30, 30, 30, 30]};
    pdfMake.createPdf(docDef).open();
  }

  print(): void {
    window.print();
  }

  // Delegates to the one shared implementation so this file cannot drift from the other exporters.
  private csvRow(valuesP: unknown[]): string {
    return this.utilService.csvRow(valuesP);
  }
}
